import React from 'react';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {Check, LoaderCircle} from 'lucide-react';
import {LanguageItem} from 'utils/i18n/types';
import {preloadAndSetDayJsLocale} from '@chekin/core';
import {cn, ResponsiveSheet, SearchField, showToast} from '@chekinapp/ui';
import {
  appLocaleNamespaces,
  getLanguageOption,
  languageOptions,
} from 'utils/i18n/constants';
import {useCurrentLanguage} from 'hooks/useCurrentLanguage';
import {SectionLabel} from 'components/SectionLabel';
import {useLanguageModalStore} from './useLanguageModalStore';

const LanguageSectionLabel = ({children}: {children: React.ReactNode}) => (
  <SectionLabel size="sm" className="mb-1.5 mt-3 first:mt-0">
    {children}
  </SectionLabel>
);

function LanguageModal() {
  const {t} = useTranslation();
  const isOpenLanguageModal = useLanguageModalStore(state => state.isOpen);
  const closeLanguageModal = useLanguageModalStore(state => state.close);

  const [query, setQuery] = React.useState('');
  const activeLanguageOption = useCurrentLanguage();
  const [pendingLanguage, setPendingLanguage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpenLanguageModal) setQuery('');
  }, [isOpenLanguageModal]);

  const changeLanguage = React.useCallback(
    async (language: LanguageItem) => {
      if (pendingLanguage) {
        return;
      }

      if (language.value === i18n.language.slice(0, 2)) {
        closeLanguageModal();
        return;
      }

      setPendingLanguage(language.value);

      try {
        await Promise.all([
          preloadAndSetDayJsLocale(language.value),
          i18n.reloadResources(language.value, appLocaleNamespaces),
        ]);
        await i18n.changeLanguage(language.value);
        showToast.success(i18n.t('language_changed_toast', {language: language.label}));
        closeLanguageModal();
      } catch (error) {
        console.warn(`Failed to switch language to ${language.value}:`, error);
      } finally {
        setPendingLanguage(null);
      }
    },
    [pendingLanguage, closeLanguageModal],
  );

  const handleClose = React.useCallback(() => {
    if (!pendingLanguage) {
      closeLanguageModal();
    }
  }, [pendingLanguage, closeLanguageModal]);

  const normalizedQuery = query.trim().toLowerCase();
  const isSearching = Boolean(normalizedQuery);
  const filteredOptions = isSearching
    ? languageOptions.filter(option =>
        `${option.label} ${option.country} ${option.value}`
          .toLowerCase()
          .includes(normalizedQuery),
      )
    : languageOptions;

  const deviceLanguage = React.useMemo(() => getLanguageOption(navigator.language), []);

  const suggestedOptions = React.useMemo(() => {
    const options = [activeLanguageOption];
    if (deviceLanguage && deviceLanguage.value !== activeLanguageOption.value) {
      options.push(deviceLanguage);
    }
    return options;
  }, [activeLanguageOption, deviceLanguage]);

  const restOptions = React.useMemo(
    () =>
      languageOptions.filter(
        option => !suggestedOptions.some(suggested => suggested.value === option.value),
      ),
    [suggestedOptions],
  );

  const renderOption = (option: LanguageItem) => {
    const selected = activeLanguageOption.value === option.value;
    const isPending = pendingLanguage === option.value;
    const meta = selected
      ? t('language_meta_current')
      : option.value === deviceLanguage?.value
      ? t('language_meta_device')
      : null;

    return (
      <button
        type="button"
        key={option.value}
        onClick={() => changeLanguage(option)}
        aria-pressed={selected}
        aria-busy={isPending}
        disabled={Boolean(pendingLanguage)}
        className={cn(
          'group flex items-center gap-[11px] rounded-[12px] px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-color-brand-blue)] kiosko:p-4',
          selected || isPending
            ? 'bg-[var(--chekin-color-surface-pressed)]'
            : 'hover:bg-[var(--chekin-color-surface-input-empty)]',
          pendingLanguage && !isPending && 'cursor-wait opacity-70',
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--chekin-neutral-50)] [box-shadow:inset_0_0_0_1px_color-mix(in_srgb,var(--chekin-color-brand-navy)_8%,transparent)]">
          <img src={option.flag} alt="" aria-hidden className="size-full object-cover" />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-[14px] leading-[18px] text-[var(--chekin-color-brand-navy)]',
              selected ? 'font-bold' : 'font-semibold',
            )}
          >
            {option.label}
          </span>
          <span className="block truncate text-[12px] leading-4 text-[var(--chekin-color-gray-2)]">
            {meta ? `${option.country} · ${meta}` : option.country}
          </span>
        </span>
        {isPending ? (
          <LoaderCircle
            size={16}
            className="shrink-0 animate-spin text-[var(--chekin-color-brand-blue)]"
          />
        ) : selected ? (
          <Check size={16} className="shrink-0 text-[var(--chekin-color-brand-blue)]" />
        ) : null}
      </button>
    );
  };

  return (
    <ResponsiveSheet
      open={isOpenLanguageModal}
      onClose={handleClose}
      closeOnOverlayClick={!pendingLanguage}
      title={t('select_your_language')}
      titleClassName="pr-8 text-left kiosko:text-kiosko-m kiosko:mb-[68px]"
      description={t('language_modal_subtitle')}
      descriptionClassName="text-[12px] leading-[17px] text-[var(--chekin-color-gray-1)]"
      className="language-modal"
    >
      <div className="flex flex-col gap-4">
        <SearchField
          tone="sunken"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onClear={() => setQuery('')}
          placeholder={t('search_language')}
          aria-label={t('search_language')}
          disabled={Boolean(pendingLanguage)}
        />

        <div className="-mr-1 max-h-[min(56vh,420px)] overflow-y-auto pr-1">
          {filteredOptions.length === 0 ? (
            <p className="py-10 text-center text-[14px] text-[var(--chekin-color-gray-1)]">
              {t('no_languages_found')}
            </p>
          ) : isSearching ? (
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {filteredOptions.map(renderOption)}
            </div>
          ) : (
            <>
              <LanguageSectionLabel>
                {t('language_section_suggested')}
              </LanguageSectionLabel>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {suggestedOptions.map(renderOption)}
              </div>
              <LanguageSectionLabel>{t('language_section_all')}</LanguageSectionLabel>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {restOptions.map(renderOption)}
              </div>
            </>
          )}
        </div>
      </div>
    </ResponsiveSheet>
  );
}

export {LanguageModal};
